import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { 
  UserIcon,
  KeyIcon,
  BellIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import { useLanguageStore } from '../../stores/languageStore';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import GoogleAddressInput from '../../components/ui/GoogleAddressInput';
import type { FormattedAddress } from '../../hooks/useGoogleMapsApi';
import { SupportedCountry } from '@tvia-ai/shared';

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { user, updateProfile, changeLanguage, isLoading } = useAuthStore();
  const { language, setLanguage } = useLanguageStore();
  
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'notifications' | 'preferences'>('profile');
  const [isUpdating, setIsUpdating] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    idNumber: user?.idNumber || '',
    address: user?.address || '',
    country: user?.country || '',
    bio: user?.bio || '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    lawsuitUpdates: true,
    marketingEmails: false,
    weeklyDigest: true,
  });

  const countries = [
    { code: 'US', name: t('countries.us', 'United States') },
    { code: 'UK', name: t('countries.uk', 'United Kingdom') },
    { code: 'IL', name: t('countries.il', 'Israel') },
    { code: 'AE', name: t('countries.ae', 'United Arab Emirates') },
    { code: 'DE', name: t('countries.de', 'Germany') },
    { code: 'FR', name: t('countries.fr', 'France') },
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'he', name: 'עברית' },
    { code: 'ar', name: 'العربية' },
    { code: 'de', name: 'Deutsch' },
    { code: 'fr', name: 'Français' },
  ];

  const tabs = [
    { id: 'profile', name: t('profile.personalInfo', 'Personal Information'), icon: UserIcon },
    { id: 'security', name: t('profile.security', 'Security'), icon: KeyIcon },
    { id: 'notifications', name: t('profile.notifications', 'Notifications'), icon: BellIcon },
    { id: 'preferences', name: t('profile.preferences', 'Preferences'), icon: GlobeAltIcon },
  ];

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdating(true);
    setError(null);

    try {
      await updateProfile({
        ...profileData,
        country: profileData.country as SupportedCountry,
      });
      setSuccessMessage(t('profile.updateSuccess', 'Profile updated successfully'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError(t('profile.updateError', 'Failed to update profile'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdating(true);
    setError(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError(t('auth.passwordsDontMatch', 'Passwords don\'t match'));
      setIsUpdating(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError(t('auth.passwordTooShort', 'Password must be at least 6 characters'));
      setIsUpdating(false);
      return;
    }

    try {
      // TODO: Implement password change API call
      setSuccessMessage(t('profile.passwordChanged', 'Password changed successfully'));
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to change password:', err);
      setError(t('profile.passwordChangeError', 'Failed to change password'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleLanguageChange = async (newLanguage: string) => {
    const selectedLanguage = newLanguage as any;
    
    // Update UI language immediately
    setLanguage(selectedLanguage);
    setSuccessMessage(t('profile.languageChanged', 'Language preference updated'));
    setTimeout(() => setSuccessMessage(null), 3000);
    
    // Then try to save to backend
    try {
      await changeLanguage(selectedLanguage);
    } catch (err) {
      console.error('Failed to save language preference to server:', err);
      // Note: We don't show an error to the user or revert the UI change
      // The language change in the UI still works even if the server save fails
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('profile.profile', 'Profile')} - tvia.ai</title>
        <meta name="description" content={t('profile.profileDescription', 'Manage your account settings and preferences')} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              {t('profile.profile', 'Profile')}
            </h1>
            <p className="mt-2 text-lg text-gray-600">
              {t('profile.manageAccount', 'Manage your account settings and preferences')}
            </p>
          </div>

          {/* Success/Error Messages */}
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex">
                <ShieldCheckIcon className="h-5 w-5 text-green-400 mr-3" />
                <p className="text-sm text-green-800">{successMessage}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                      activeTab === tab.id
                        ? 'bg-cyan-100 text-cyan-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <tab.icon className="h-5 w-5 mr-3" />
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="bg-white shadow rounded-lg">
                {/* Personal Information Tab */}
                {activeTab === 'profile' && (
                  <form onSubmit={handleProfileSubmit} className="p-6">
                    <div className="border-b border-gray-200 pb-6 mb-6">
                      <h2 className="text-xl font-semibold text-gray-900">
                        {t('profile.personalInfo', 'Personal Information')}
                      </h2>
                      <p className="mt-1 text-sm text-gray-600">
                        {t('profile.personalInfoDescription', 'Update your personal details and contact information')}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                          {t('auth.firstName', 'First Name')}
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          value={profileData.firstName}
                          onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                          {t('auth.lastName', 'Last Name')}
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          value={profileData.lastName}
                          onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                          {t('auth.email', 'Email')}
                        </label>
                        <input
                          type="email"
                          id="email"
                          value={profileData.email}
                          onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                          {t('profile.phone', 'Phone Number')}
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          value={profileData.phone}
                          onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label htmlFor="idNumber" className="block text-sm font-medium text-gray-700">
                          {t('profile.idNumber', 'ID Number')}
                        </label>
                        <input
                          type="text"
                          id="idNumber"
                          value={profileData.idNumber}
                          onChange={(e) => setProfileData(prev => ({ ...prev, idNumber: e.target.value }))}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                          placeholder={t('profile.idNumberPlaceholder', 'National ID, SSN, or other identification number')}
                        />
                      </div>

                      <div className="md:col-span-2">
                        <GoogleAddressInput
                          label={t('profile.address', 'Address')}
                          value={profileData.address}
                          onChange={(value) => setProfileData(prev => ({ ...prev, address: value }))}
                          onAddressSelect={(address: FormattedAddress) => {
                            setProfileData(prev => ({
                              ...prev,
                              address: address.formattedAddress,
                            }));
                          }}
                          placeholder={t('profile.addressPlaceholder', 'Enter your complete address')}
                          addressType="personal"
                          helpText={t('profile.addressHelp', 'Enter your complete residential address using Google Maps location search.')}
                          className="w-full"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                          {t('profile.country', 'Country')}
                        </label>
                        <select
                          id="country"
                          value={profileData.country}
                          onChange={(e) => setProfileData(prev => ({ ...prev, country: e.target.value }))}
                          className="mt-1 block w-full px-4 py-3 border-gray-300 rounded-lg shadow-sm focus:ring-cyan-500 focus:border-cyan-500 text-base min-h-[48px]"
                        >
                          <option value="">{t('common.selectOption', 'Select an option')}</option>
                          {countries.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="md:col-span-2">
                        <label htmlFor="bio" className="block text-sm font-medium text-gray-700">
                          {t('profile.bio', 'Bio')}
                        </label>
                        <textarea
                          id="bio"
                          rows={4}
                          value={profileData.bio}
                          onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                          className="mt-1 block w-full px-4 py-3 border-gray-300 rounded-lg shadow-sm focus:ring-cyan-500 focus:border-cyan-500 text-base"
                          placeholder={t('profile.bioPlaceholder', 'Tell us a bit about yourself...')}
                        />
                      </div>
                    </div>

                    <div className="pt-6 border-t border-gray-200 mt-6">
                      <button
                        type="submit"
                        disabled={isUpdating}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
                      >
                        {isUpdating ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          t('common.save', 'Save Changes')
                        )}
                      </button>
                    </div>
                  </form>
                )}

                {/* Security Tab */}
                {activeTab === 'security' && (
                  <form onSubmit={handlePasswordSubmit} className="p-6">
                    <div className="border-b border-gray-200 pb-6 mb-6">
                      <h2 className="text-xl font-semibold text-gray-900">
                        {t('profile.security', 'Security')}
                      </h2>
                      <p className="mt-1 text-sm text-gray-600">
                        {t('profile.securityDescription', 'Manage your password and security settings')}
                      </p>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                          {t('profile.currentPassword', 'Current Password')}
                        </label>
                        <input
                          type="password"
                          id="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                          {t('profile.newPassword', 'New Password')}
                        </label>
                        <input
                          type="password"
                          id="newPassword"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                          {t('auth.confirmPassword', 'Confirm New Password')}
                        </label>
                        <input
                          type="password"
                          id="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        />
                      </div>
                    </div>

                    <div className="pt-6 border-t border-gray-200 mt-6">
                      <button
                        type="submit"
                        disabled={isUpdating}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
                      >
                        {isUpdating ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          t('profile.changePassword', 'Change Password')
                        )}
                      </button>
                    </div>
                  </form>
                )}

                {/* Notifications Tab */}
                {activeTab === 'notifications' && (
                  <div className="p-6">
                    <div className="border-b border-gray-200 pb-6 mb-6">
                      <h2 className="text-xl font-semibold text-gray-900">
                        {t('profile.notifications', 'Notifications')}
                      </h2>
                      <p className="mt-1 text-sm text-gray-600">
                        {t('profile.notificationsDescription', 'Choose how you want to be notified about updates')}
                      </p>
                    </div>

                    <div className="space-y-6">
                      {Object.entries(notificationSettings).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between">
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">
                              {t(`profile.${key}`, key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()))}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {t(`profile.${key}Description`, '')}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setNotificationSettings(prev => ({ ...prev, [key]: !value }))}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 ${
                              value ? 'bg-cyan-600' : 'bg-gray-200'
                            }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                value ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preferences Tab */}
                {activeTab === 'preferences' && (
                  <div className="p-6">
                    <div className="border-b border-gray-200 pb-6 mb-6">
                      <h2 className="text-xl font-semibold text-gray-900">
                        {t('profile.preferences', 'Preferences')}
                      </h2>
                      <p className="mt-1 text-sm text-gray-600">
                        {t('profile.preferencesDescription', 'Customize your experience')}
                      </p>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <label htmlFor="language" className="block text-sm font-medium text-gray-700">
                          {t('profile.language', 'Language')}
                        </label>
                        <select
                          id="language"
                          value={language}
                          onChange={(e) => handleLanguageChange(e.target.value)}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        >
                          {languages.map((lang) => (
                            <option key={lang.code} value={lang.code}>
                              {lang.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfilePage; 